import { Form, Input, Modal, Row, Col, Button } from 'antd';
import { useCallback, useRef, useState } from 'react';
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from 'react-google-recaptcha-v3';

import { reserveProduct } from 'actions/products';

import FormInput from 'components/formInput';
import { ThankYouMessage } from 'components/thankYouMessage';
import { CloseModalSVG } from 'components/iconsSVG/close';
import { useI18n } from 'context/i18n';

import { useValidate } from 'hooks';
import { handleReCaptcha } from 'utils/recaptcha';
import { StoreStatus } from 'components/store-status';

import storeStyles from 'components/storesGrid/style.module.css';

const ReserveModal = ({ reserve, visible, onClose, productId }) => {
  const { t, locale } = useI18n();
  const validate = useValidate();
  const formRef = useRef(null);
  const { executeRecaptcha } = useGoogleReCaptcha();
  const handleReCaptchaVerify = useCallback(async () => handleReCaptcha(executeRecaptcha), [executeRecaptcha]);

  const [submitted, setSubmitted] = useState(false);
  const [fields] = useState([
    {
      name: ['name'],
      value: '',
    },
    {
      name: ['email'],
      value: reserve.email,
    },
    {
      name: ['telephone'],
      value: '',
    },
  ]);

  const handleSubmit = async (values) => {
    try {
      const token = await handleReCaptchaVerify();
      const { name, telephone } = values;
      let shouldReserve = false;

      if (token) {
        if (name && telephone) {
          shouldReserve = true;
        }
      }

      if (!shouldReserve) {
        throw new Error('Failed captcha verification.');
      } else {
        const reserveResult = await reserveProduct({
          profile: locale,
          store_id: reserve.store.id,
          email: reserve.email,
          sku: productId,
          phone: telephone,
          name,
        });

        if (reserveResult) {
          setSubmitted(true);
        } else {
          throw new Error('An error occurred while reserving the product.');
        }
      }
    } catch (e) {
      console.error(e);
      Modal.error({
        title: t('generico', 'error.api.modal.titulo', 'Se ha producido un error'),
        content: e.toString(),
      });
    }
  };

  const { store } = reserve;

  return (
    <Modal
      width={'640px'}
      title={null}
      visible={visible}
      centered={true}
      closable={true}
      destroyOnClose={true}
      onCancel={onClose}
      footer={null}
      closeIcon={<CloseModalSVG />}
    >
      {submitted ?
        <ThankYouMessage className="flex flex-col items-center justify-center" type="reserve" />
      : <>
          <p className="flex justify-center font-bold">{t('ficha.producto', 'product.reserve')}</p>
          <p className="mb-0 flex justify-center">{t('ficha.producto', 'product.reserve.thanks')}</p>

          <div className={`${storeStyles.storeCard} relative left-[calc(50%-100px)] !m-[0px] max-w-[275px]`}>
            <div className={storeStyles.cardName}>
              <a href={`/${locale}/shops${store.url}`}>{store.name}</a>
            </div>
            <div className={storeStyles.cardAddress}>
              <span className={storeStyles.address}>{`${store.address1}, ${store.postalCode}, ${store.regionName}, ${store.countryCode}`}</span>
            </div>
            {store.status && store.statusText ?
              <StoreStatus status={store.status} statusText={store.statusText} />
            : null}
          </div>

          <p className="mb-[34px] mt-[10px] flex justify-center text-center">{t('ficha.producto', 'product.reserve.form.info')}</p>

          <Form
            defaultValue={{
              email: reserve.email,
            }}
            fields={fields}
            className="customForm"
            onFinish={handleSubmit}
            ref={formRef}
          >
            <Row gutter={[8, 0]}>
              <Col md={24} xs={24}>
                <FormInput rules={validate('name')} name="name" floatLabel={t('generico', 'input.name', 'Name')}>
                  <Input />
                </FormInput>
              </Col>
            </Row>
            <Row>
              <Col md={12} xs={24}>
                <FormInput rules={validate('email')} name="email" floatLabel={t('generico', 'input.email', 'Email')}>
                  <Input disabled value={reserve.email} readOnly />
                </FormInput>
              </Col>
              <Col md={12} xs={24}>
                <FormInput rules={validate('telephone')} name="telephone" floatLabel={t('generico', 'input.phone', 'Phone')}>
                  <Input />
                </FormInput>
              </Col>
            </Row>
            <Button className="m-auto !flex items-center !px-8 !py-4" type="default" htmlType="submit">
              {t('generico', 'send', 'Send')}
            </Button>
          </Form>
        </>
      }
    </Modal>
  );
};

const WrapperReserveModal = (props) => {
  const { locale } = useI18n();
  const { visible } = props;

  return visible ?
      <GoogleReCaptchaProvider
        reCaptchaKey={process.env.RECAPTCHA_KEY}
        language={locale}
        useRecaptchaNet
        scriptProps={{
          async: false,
          defer: false,
          appendTo: 'body',
          nonce: undefined,
        }}
      >
        <ReserveModal {...props} />
      </GoogleReCaptchaProvider>
    : <ReserveModal {...props} />;
};

export default WrapperReserveModal;
